import React from 'react';
import cx from 'classnames';

import styles from './paragraphs.module.less';

export type ParagraphColor = 'BLACK_100' | 'BLACK_075' | 'BLACK_050' | 'WHITE_100';
export type ParagraphSize = 'large' | 'base' | 'small';

type ParagraphElementProps = React.HTMLAttributes<HTMLParagraphElement & HTMLDivElement> & {
  component?: 'p' | 'div';
  size?: ParagraphSize;
  color?: ParagraphColor;
  strong?: boolean;
};

type ParagraphProps = Omit<ParagraphElementProps, 'size'>;

const SIZE_LOOKUP: Record<ParagraphSize, string> = {
  base: styles.base,
  small: styles.small,
  large: styles.large,
};

const COLOR_LOOKUP: Record<ParagraphColor, string | undefined> = {
  BLACK_100: undefined,
  BLACK_075: styles.black075,
  BLACK_050: styles.black050,
  WHITE_100: styles.white100,
};

/**
 * Zola-UI 3.0 text element.
 *
 * By default, a paragraph element, but can be a p / span / div etc.  Applies the
 * appropriate font size, removes margin/padding, optionally sets the color of the text.
 *
 * @example
 * <BodyBase color="BLACK_100">For all the days along the way</BodyBase/>
 *
 */
export const ParagraphElement = ({
  component: Component = 'p',
  size = 'base',
  color = 'BLACK_100',
  strong,
  className,
  ...rest
}: ParagraphElementProps): JSX.Element => {
  return (
    <Component
      className={cx(SIZE_LOOKUP[size], COLOR_LOOKUP[color], { [styles.strong]: strong }, className)}
      {...rest}
    />
  );
};

export const BodyLarge = (props: ParagraphProps): JSX.Element => (
  <ParagraphElement size="large" {...props} />
);

export const BodyBase = (props: ParagraphProps): JSX.Element => <ParagraphElement {...props} />;

export const BodySmall = (props: ParagraphProps): JSX.Element => (
  <ParagraphElement size="small" {...props} />
);

export default {
  BodyLarge,
  BodyBase,
  BodySmall,
};
